<template>
  <div class="modal-card" style="width: auto">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="truck" size="is-small"></b-icon>
        รายละเอียดออเดอร์ WeServe
      </p>
      <button type="button" class="delete" @click="$emit('close')"></button>
    </header>

    <section class="modal-card-body">
      <div v-if="isLoading" class="has-text-centered">
        <b-loading :is-full-page="false" v-model="isLoading"></b-loading>
        <p>กำลังโหลดข้อมูลออเดอร์...</p>
      </div>

      <div v-else-if="orderDetails" class="order-details">
        <!-- Order Information -->
        <div class="box">
          <h5 class="title is-5">ข้อมูลออเดอร์</h5>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">รหัสออเดอร์ WeServe</label>
                <div class="control copy-field">
                  <b-tag type="is-primary" size="is-medium">
                    {{ orderDetails.order_code }}
                  </b-tag>
                  <b-button
                    size="is-small"
                    type="is-primary"
                    outlined
                    icon-pack="fas"
                    icon-right="copy"
                    @click="copyToClipboard(orderDetails.order_code, 'รหัสออเดอร์')"
                  />
                </div>
              </div>
              <div class="field">
                <label class="label">รหัสออเดอร์วิลล่า</label>
                <div class="copy-field">
                  <p>{{ villaOrderID }}</p>
                  <b-button
                    size="is-small"
                    type="is-info"
                    outlined
                    icon-pack="fas"
                    icon-right="copy"
                    @click="copyToClipboard(villaOrderID, 'รหัสออเดอร์วิลล่า')"
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">สถานะ</label>
                <b-tag
                  :type="getStatusTagType(orderDetails.order_status_name)"
                  size="is-medium"
                >
                  {{ orderDetails.order_status_name }}
                </b-tag>
              </div>
              <div class="field">
                <label class="label">ประเภทบริการ</label>
                <p>{{ orderDetails.order_type_name }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing Information -->
        <div class="box">
          <h5 class="title is-5">รายละเอียดราคา</h5>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">ค่าบริการขนส่ง</label>
                <p class="has-text-weight-bold">{{ formatPrice(orderDetails.order_transfer_price) }} ฿</p>
              </div>
              <div class="field">
                <label class="label">จุดเพิ่มเติม</label>
                <p>{{ formatPrice(orderDetails.order_transfer_extra_price) }} ฿</p>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">บริการเสริม</label>
                <p>{{ formatPrice(orderDetails.order_total_addon_price) }} ฿</p>
              </div>
              <div class="field">
                <label class="label">ราคารวม</label>
                <p class="has-text-weight-bold has-text-primary title is-4">
                  {{ formatPrice(orderDetails.order_net_price) }} ฿
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Vehicle Information -->
        <div class="box" v-if="orderDetails.order_vehicle_type">
          <h5 class="title is-5">ข้อมูลรถ</h5>
          <div class="media">
            <div class="media-content">
              <p class="title is-6">{{ orderDetails.order_vehicle_type.vehicle_title_en }}</p>
              <p class="subtitle is-7">{{ orderDetails.order_vehicle_type.vehicle_title_th }}</p>
              <div class="tags">
                <span class="tag is-info is-light">
                  Size: {{ orderDetails.order_vehicle_type.vehicle_size_carry }}
                </span>
                <span class="tag is-success is-light">
                  Weight: {{ orderDetails.order_vehicle_type.vehicle_weight_carry }} kg
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Driver Information -->
        <div class="box" v-if="orderDetails.order_rider_full_name">
          <h5 class="title is-5">ข้อมูลคนขับ</h5>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">ชื่อคนขับ</label>
                <p>{{ orderDetails.order_rider_full_name }}</p>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label">เบอร์โทรคนขับ</label>
                <p>
                  <a :href="`tel:${orderDetails.order_rider_phone}`">
                    {{ orderDetails.order_rider_phone }}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Delivery Points -->
        <div class="box" v-if="orderDetails.order_point && orderDetails.order_point.length">
          <h5 class="title is-5">เส้นทางการจัดส่ง</h5>
          <div
            v-for="(point, index) in orderDetails.order_point"
            :key="index"
            class="point-item"
          >
            <div class="timeline-item">
              <div class="timeline-marker">
                <b-icon
                  :icon="index === 0 ? 'play-circle' : index === orderDetails.order_point.length - 1 ? 'stop-circle' : 'circle'"
                  :type="index === 0 ? 'is-success' : index === orderDetails.order_point.length - 1 ? 'is-danger' : 'is-info'"
                ></b-icon>
              </div>
              <div class="timeline-content">
                <p class="heading">Point {{ point.order_point_number }}</p>
                <div class="content">
                  <p><strong>{{ point.order_point_address }}</strong></p>
                  <p>Contact: {{ point.order_point_list_tel }}</p>
                  <p v-if="point.order_point_remark" class="has-text-grey">
                    Note: {{ point.order_point_remark }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Services -->
        <div class="box" v-if="orderDetails.order_addon && orderDetails.order_addon.length">
          <h5 class="title is-5">บริการเสริม</h5>
          <div class="addon-list">
            <div
              v-for="addon in orderDetails.order_addon"
              :key="addon.order_addon_id"
              class="addon-item"
            >
              <div class="columns is-vcentered">
                <div class="column">
                  <p class="has-text-weight-semibold">{{ addon.order_addon_addon.addon_title_en }}</p>
                  <p class="is-size-7 has-text-grey">{{ addon.order_addon_addon.addon_title_th }}</p>
                </div>
                <div class="column is-narrow">
                  <span class="tag is-primary">{{ formatPrice(addon.order_addon_price) }} ฿</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Notes -->
        <div class="box" v-if="orderDetails.order_note">
          <h5 class="title is-5">หมายเหตุ</h5>
          <div class="content">
            <p>{{ orderDetails.order_note }}</p>
          </div>
        </div>

        <!-- Timeline -->
        <div class="box" v-if="orderDetails.order_time_start">
          <h5 class="title is-5">กำหนดการ</h5>
          <div class="field">
            <label class="label">เวลาเริ่มต้นที่กำหนด</label>
            <p>{{ formatDateTime(orderDetails.order_time_start) }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="has-text-centered">
        <b-icon icon="exclamation-triangle" size="is-large" type="is-danger"></b-icon>
        <p class="has-text-danger">{{ error }}</p>
        <b-button @click="loadOrderDetails" type="is-primary" outlined>
          Retry
        </b-button>
      </div>
    </section>

    <footer class="modal-card-foot">
      <b-button @click="$emit('close')">Close</b-button>
      <b-button
        v-if="orderDetails && orderDetails.order_code"
        @click="refreshDetails"
        type="is-info"
        :loading="isLoading"
      >
        Refresh
      </b-button>
    </footer>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "WeServeOrderDetailsModal",
  props: {
    weserveOrderCode: {
      type: String,
      required: true
    },
    villaOrderID: {
      type: String,
      required: true
    },
    provinceName: {
      type: String,
      default: "phuket"
    }
  },
  data() {
    return {
      isLoading: false,
      orderDetails: null,
      error: null
    };
  },
  mounted() {
    this.loadOrderDetails();
  },
  methods: {
    async loadOrderDetails() {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await this.$store.dispatch("weserve/getOrderDetail", {
          weserveOrderCode: this.weserveOrderCode,
          provinceName: this.provinceName
        });

        if (response && response.success) {
          this.orderDetails = response.data;
        } else {
          this.error = "Failed to load order details";
        }
      } catch (error) {
        console.error("Error loading order details:", error);
        this.error = error.message || "Failed to load order details";
      } finally {
        this.isLoading = false;
      }
    },
    async refreshDetails() {
      await this.loadOrderDetails();
    },
    getStatusTagType(status) {
      const statusMap = {
        "Order Made": "is-warning",
        "Assigning Driver": "is-warning",
        "Driver Assigned": "is-info",
        "Driver Accepted": "is-info",
        "On The Way": "is-info",
        "Arrived": "is-primary",
        "Picked Up": "is-primary",
        "Completed": "is-success",
        "Cancelled": "is-danger",
        "Rejected": "is-danger"
      };
      return statusMap[status] || "is-light";
    },
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },
    formatDateTime(dateTime) {
      return dayjs(dateTime).format("DD/MM/YYYY HH:mm");
    },
    async copyToClipboard(text, label) {
      try {
        // Use the Clipboard API if available
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          textArea.remove();
        }

        this.$buefy.toast.open({
          message: `${label} copied to clipboard!`,
          type: "is-success",
          duration: 2000,
          position: "is-top"
        });
      } catch (error) {
        console.error("Failed to copy:", error);
        this.$buefy.toast.open({
          message: "Failed to copy to clipboard",
          type: "is-danger",
          duration: 2000,
          position: "is-top"
        });
      }
    }
  }
};
</script>

<style scoped>
.order-details {
  max-height: 70vh;
  overflow-y: auto;
}

.copy-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy-field p,
.copy-field .tag {
  flex: 1;
  margin: 0;
}

.copy-field .button {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.copy-field .button:hover {
  transform: scale(1.1);
}

.point-item {
  margin-bottom: 1rem;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
}

.timeline-marker {
  margin-right: 1rem;
  margin-top: 0.25rem;
}

.timeline-content {
  flex: 1;
}

.addon-item {
  padding: 0.75rem;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.box {
  margin-bottom: 1.5rem;
}

.modal-card {
  min-width: 600px;
  max-width: 90vw;
}

@media screen and (max-width: 768px) {
  .modal-card {
    min-width: 90vw;
  }
}
</style>